import { useContext, useEffect, useRef, useState } from "react";
import { ReactSortable, Sortable, Store } from "react-sortablejs";
import { sortableOptions } from "../Draggable/Draggable";
import styled, { useTheme } from "styled-components";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import Icon from "../Icon/Icon";
import TextField from "../FormFields/TextField";
import { FocusBorder } from "../FormFields/FieldTemplate";
import { DragCard } from "../Draggable/Draggable";
import { UpdatePropertyRequest } from "../../views/Entries/manageEntry";
import useTaskHandler, {
  TASKS_COMPLETED,
  TaskHandlerReturnType,
} from "../../hooks/useTaskHandler";
import { useAlert } from "../Alert/Alerts";
import {
  DynamicDropdownOption,
  updateDynamicDropdownOption,
} from "../../views/Admin/DynamicDropdowns/manageDynamicDropdowns";

export const StyledOptionCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colorBackgroundLight};
  padding: 0.5rem;
  padding-left: calc(0.5rem + 2px);
  border: solid 1px ${({ theme }) => theme.colorBorderLight};
  cursor: pointer;

  .option-display {
    display: flex;
    align-items: center;
    min-height: 24px;
    margin-right: auto;
  }
`;

interface DragDropdownOptionListProps {
  dropdownOptions: DynamicDropdownOption[];
  setSelectedOption(option: DynamicDropdownOption): void;
  onEdit(updated: { id: number; name: string }): void;
  onDelete(id: number): void;
}

export default function DragDropdownOptionList({
  dropdownOptions,
  setSelectedOption,
  onEdit,
  onDelete,
}: DragDropdownOptionListProps) {
  const { addNewAlert } = useAlert();
  const theme = useTheme();
  const ref = useRef<DynamicDropdownOption[] | null>(null);
  const [options, setOptions] = useState<DynamicDropdownOption[]>([]);

  const {
    reset,
    onSubmit,
    ...state
  }: TaskHandlerReturnType<UpdatePropertyRequest> =
    useTaskHandler<UpdatePropertyRequest>();

  useEffect(() => {
    if (dropdownOptions) {
      ref.current = dropdownOptions;
      setOptions(dropdownOptions);
    }
  }, [dropdownOptions]);

  useEffect(() => {
    if (state.status === TASKS_COMPLETED) {
      if (state.errored.length) {
        addNewAlert({ type: "error", message: "Failed to update order." });
      } else {
        addNewAlert({
          type: "success",
          message: "Successfully updated order.",
        });
      }
      reset();
    }
  }, [state.status]);

  const handleReorder = (
    newOrder: DynamicDropdownOption[],
    _sortable: Sortable | null,
    store: Store
  ) => {
    if (
      store.dragging &&
      JSON.stringify(store.dragging.props?.list) !== JSON.stringify(newOrder)
    ) {
      if (ref.current?.length === newOrder.length) {
        const list = newOrder.map((opt, i) => ({
          id: opt.id,
          propertyName: "order",
          propertyValue: i,
        }));
        setOptions(newOrder);
        onSubmit(updateDynamicDropdownOption, list);
      }
    }
    ref.current = newOrder;
  };

  return (
    <ReactSortable
      list={options}
      setList={(newList, sortable, store) =>
        handleReorder(newList, sortable, store)
      }
      {...sortableOptions({
        group: "dropdown-options",
        name: "dropdown-options",
        drop: false,
        sortable: true,
      })}
    >
      {options.length > 0 ? (
        options.map((option) => (
          <DragDropdownCard
            key={option.id}
            option={option}
            onEdit={onEdit}
            onDelete={onDelete}
            setSelected={setSelectedOption}
          />
        ))
      ) : (
        <p>No options found</p>
      )}
    </ReactSortable>
  );
}

function DragDropdownCard({
  option,
  onEdit,
  onDelete,
  setSelected,
}: {
  option: DynamicDropdownOption;
  onEdit(updated: { id: number; name: string }): void;
  onDelete(id: number): void;
  setSelected(option: DynamicDropdownOption): void;
}) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);

  const formik = useFormik({
    initialValues: option,
    enableReinitialize: true,
    onSubmit: () => {},
    validationSchema: Yup.object({
      name: Yup.string().required("Required"),
    }),
  });

  return (
    <FormikProvider value={formik}>
      <StyledOptionCard
        className="draggable"
        onClick={() => setSelected(option)}
      >
        {/* <FocusBorder /> */}
        {isEditing ? (
          <>
            <TextField
              name="name"
              value={formik.values.name}
              className="mr-auto"
              placeholder="Option Name"
              hideShadow
            />
            <Icon
              icon="check"
              className="self-center"
              onClick={() => {
                onEdit({ id: option.id, name: formik.values.name });
                setIsEditing(false);
              }}
              color={theme.colorActivation}
              width="18px"
              height="18px"
            />
            <Icon
              icon="close"
              className="self-center"
              onClick={() => {
                setIsEditing(false);
                formik.resetForm();
              }}
              color={theme.colorPrimary}
              width="25px"
              height="25px"
            />
          </>
        ) : (
          <>
            <p className="option-display">{option.name}</p>
            <Icon
              icon="edit"
              className="self-center"
              color={theme.colorPrimary}
              onClick={() => setIsEditing(true)}
              width="18px"
              height="18px"
            />
          </>
        )}
        <Icon
          icon="trash"
          className="self-center"
          color={theme.colorPrimary}
          onClick={() => onDelete(option.id)}
          width="18px"
          height="18px"
        />
      </StyledOptionCard>
    </FormikProvider>
  );
}
